import React, {
  useEffect,
  createContext,
  useState
} from 'react'
import {
  BrowserRouter,
  Route,
  Routes
} from 'react-router-dom'
import { useMsal } from '@azure/msal-react'
import ActiveContractLayout from './layouts/ActiveContractLayout'
import AuthLayout from './layouts/AuthLayout'
import DashboardLayout from './layouts/DashboardLayout'
import LayoutWrapper from './layouts/LayoutWrapper'
import NoAuthLayout from './layouts/NoAuthLayout'
import {
  Profile,
  RepairAndInquiries,
  RepairAndInquiriesRequest,
  RepairAndInquiriesDetail,
  AddContract,
  Home,
  Contracts
} from './pages/dashboard'
import { ContextEntity } from './customEntites/context.entity'
import LoadingOverlay from './components/LoadingOverlay'
import NotFound from './pages/NotFound'
import PrivacyPolicy from './pages/PrivacyPolicy'
import Imprint from './pages/Imprint'
import { userApi } from './api/user'
import { useTranslation } from 'react-i18next'
import useFocusedInput from './customHooks/focusedInputHook'
import { clearAzureData } from './services/azure.service'
import { analytics } from '.'
import { setPageTitle } from './services/title.service'
import { EditTheme } from './pages/dashboard/EditTheme'
import { ThemeProvider, useTheme } from './customProviders/ThemeProvider'
import { magnifiers } from './consts/fontSize'
import { logFirebaseEvent } from './services/firebase.service'

export const GlobalContext = createContext<ContextEntity>(null!)

function App() {
  console.log('test')
  const { instance, accounts, inProgress } = useMsal()
  const { activeMagnifier } = useTheme()
  const [redirect, setRedirect] = useState(false)
  const [numberOfLoad, setNumberOfLoad] = useState(0)
  const [context, setContext] = useState({
    contracts: {
      activeContracts: [],
      contracts: []
    },
    alerts: [],
    focusedElement: '' as any
  })
  const focusedElement = useFocusedInput()
  const { i18n } = useTranslation()
  const pathname = window.location.pathname
  const search = window.location.search

  useEffect(() => {
    setContext((prev: any) => ({ ...prev, focusedElement }))
  }, [focusedElement])

  useEffect(() => {
    const script = document.createElement('script')
    const url = process.env.REACT_APP_COOKIE_URL || ''
    script.src = url
    document.head.appendChild(script)
  }, [])

  useEffect(() => {
    (async () => {
      if (!pathname.includes('admin')) {
        if (accounts[0]?.environment && accounts[0]?.environment === 'login.windows.net') {
          instance.loginRedirect().catch(async (err: any) => {
            if (err?.errorCode === 'interaction_in_progress') {
              await clearAzureData()
              window.location.replace(pathname + search)
            }
          })
        }
        await instance.handleRedirectPromise().then(async (res) => {
          const idTokenClaims: any = res?.idTokenClaims
          const isForgotPassword: any = idTokenClaims?.isForgotPassword
          if (res?.authority === `${process.env.REACT_APP_AZ_EDIT_PROFILE_AUTHORITY}/`?.toLowerCase()) {
            localStorage.setItem('profile_edited', '1')
          }
          if (res?.authority === `${process.env.REACT_APP_AZ_CHANGE_EMAIL_AUTHORITY}/`?.toLowerCase()) {
            localStorage.setItem('profile_edited', '1')
          }
          if (res?.authority === `${process.env.REACT_APP_AZ_CHANGE_PASSWORD_AUTHORITY}/`?.toLowerCase() && numberOfLoad === 0) {
            setNumberOfLoad(1)
            await userApi.changePasswordConfirmation()
          }
          if (res?.authority === `${process.env.REACT_APP_AZ_AUTHORITY}/`?.toLowerCase()) {
            localStorage.removeItem('home_info_alert')
            if (numberOfLoad === 0 && isForgotPassword) {
              setNumberOfLoad(1)
              await userApi.changePasswordConfirmation()
            }
          }
          try {
            const idTokenClaims = res?.idTokenClaims as any;
            if (idTokenClaims?.newUser && numberOfLoad ===0) {
              setNumberOfLoad(1)
              logFirebaseEvent('ok_ad_registration', {
                timestamp: new Date().toISOString(),
                userId: res?.account?.localAccountId ?? '',
                route:
                window.location.pathname
                  .toLowerCase()
                  .replace(/[^a-zA-Z0-9]/g, '_') === '_'
                    ? 'home'
                    : window.location.pathname
                  .toLowerCase()
                  .replace(/[^a-zA-Z0-9]/g, '_')
                  .replace(/^_/, ''),
                })
              } 
          } catch(err){
            console.log('Error while sending register data to Firebase', err)
          } 
        
          if (res?.authority === `${process.env.REACT_APP_AZ_CHANGE_EMAIL_AUTHORITY}/`?.toLowerCase() && numberOfLoad === 0) {
            setNumberOfLoad(1)
            await userApi.changeEmailConfirmation()
          }
          setRedirect(true)
          const savedPath = localStorage.getItem('path')
          if (savedPath && (savedPath != '/' || savedPath.startsWith('/#state=')) && accounts.length > 0) {
            localStorage.removeItem('path')
            window.location.replace(savedPath)
          }
          if (!accounts[0] && inProgress != 'login' && redirect) {
            if (pathname != '/') {
              localStorage.setItem('path', pathname + search)
              window.location.replace('/')
            }
            const language = i18n.language
            const savedTheme = localStorage.getItem('fontSize');
            const defaultMagnifierValue = !!savedTheme
              ? JSON.parse(savedTheme)
              : magnifiers()[0]
            instance.loginRedirect({
              scopes: [],
              extraQueryParameters: {'ui_locales': `${language?.toLocaleLowerCase() || 'de'}`, 'magnifier': defaultMagnifierValue?.value}
            }).catch(async (err: any) => {
              if (err?.errorCode === 'interaction_in_progress') {
                await clearAzureData()
                window.location.replace(pathname + search)
              }
            })
          }
        }).catch((err: any) => {
          if (err?.errorMessage?.includes('AADB2C90091')) {
            window.location.reload()
          } else {
            throw err
          }
        })
      }
    })()
  }, [redirect])

  if (!accounts[0] || localStorage.getItem('path') || inProgress !== 'none') {
    return (
      <div>
        <LoadingOverlay purpose={'redirect-overlay'} />
      </div>
    )
  }

  if (accounts[0] || !localStorage.getItem('path') && pathname) {
    setPageTitle(pathname)
  }

  return (
    <div className="App">
      <BrowserRouter>
        <ThemeProvider>
          <GlobalContext.Provider value={{ context, setContext } as ContextEntity}>
            <Routes>
              <Route path='/no-auth' element={<NotFound />} />
              <Route element={<DashboardLayout />}>
                <Route element={<AuthLayout />}>
                  <Route element={<ActiveContractLayout />}>
                    <Route path='/repairs-and-inquiries' element={<RepairAndInquiries />} />
                    <Route path='/repairs-and-inquiries/:code' element={<RepairAndInquiriesDetail />} />
                    <Route path='/repairs-and-inquiries/new-request' element={<RepairAndInquiriesRequest />} />
                  </Route>
                </Route>
              </Route>
              <Route element={<LayoutWrapper />}>
                <Route element={<AuthLayout />}>
                  <Route path='/contracts/add-contract' element={<AddContract />} />
                  <Route path='/contracts' element={<Contracts />} />
                  <Route element={<ActiveContractLayout />}>
                    <Route path='/' element={<Home />} />
                  </Route>
                  <Route path='/account' element={<Profile />} />
                  <Route path='/account/edit-theme' element={<EditTheme />} />
                </Route>
                <Route element={<NoAuthLayout />}>
                </Route>
                <Route path='/privacy-policy' element={<PrivacyPolicy />} />
                <Route path='/datenschutzerklaerung' element={<PrivacyPolicy />} />
                <Route path='/imprint' element={<Imprint />} />
                <Route path='/impressum' element={<Imprint />} />
                <Route path='*' element={<NotFound />} />
              </Route>
            </Routes>
          </GlobalContext.Provider>
        </ThemeProvider>
      </BrowserRouter>
    </div>
  )
}

export default App
